// Whether the phone currently has a connection. Kept in one place because several
// screens need it and the browser only reports it through events.
//
// Worth knowing: navigator.onLine says the phone is attached to a network, not
// that our API can be reached. Venue WiFi that accepts a connection and drops all
// traffic still reads as online, which is why a failed request is treated as
// offline as well.
//
// Inside the APK it is worse than approximate, it is wrong: an Android web view reports
// the page as online with the phone in airplane mode and no network at all, and the
// offline event never fires. So on Android the answer comes from Android, which is the
// only place that knows. Everything reading this composable gets the fix: the door's
// warning, the retry of a queued support request, and what a support request reports
// about the phone.

// Attached once for the whole app. Several components read this, and each would
// otherwise add its own pair of listeners to the same shared state.
let listening = false

export const useOnline = () => {
  const online = useState('online', () => true)

  if (import.meta.client && !listening) {
    listening = true
    online.value = navigator.onLine

    const update = () => {
      online.value = navigator.onLine
    }

    // Kept on Android as well. It costs nothing, and a web view that ever does report
    // going offline is not worth ignoring.
    window.addEventListener('online', update)
    window.addEventListener('offline', update)

    // Deliberately not awaited: whoever called this needs a value now, and the phone
    // being online is the safe assumption to hold for the moment it takes Android to
    // answer. Being wrong that way round costs a failed request, which every caller
    // already handles; being wrong the other way would hide a working connection.
    const askAndroid = async () => {
      const { Capacitor } = await import('@capacitor/core')

      if (!Capacitor.isNativePlatform()) {
        return
      }

      const { Network } = await import('@capacitor/network')
      const status = await Network.getStatus()

      online.value = status.connected

      Network.addListener('networkStatusChange', (change) => {
        online.value = change.connected
      })
    }

    askAndroid().catch(() => {
      // No bridge, or Android refused to say. The web view's own answer stands, which is
      // what the app had before this existed.
    })
  }

  return online
}
